/**
 * Redux async actions (src/store/actions/coursesActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";

const publicCourseDetailFailure = (error) => ({
  status: Number(error?.response?.status) === 404 ? 404 : 0,
  code: Number(error?.response?.status) === 404
    ? "COURSE_NOT_FOUND"
    : "COURSE_UNAVAILABLE",
});

const isPublicCourseDetail = (course, courseId) => (
  Boolean(course)
  && !Array.isArray(course)
  && typeof course === 'object'
  && String(course._id || '') === courseId
  && typeof course.Course_Name === 'string'
  && Boolean(course.Course_Name.trim())
);

const adminCourseDeleteFailure = (error) => {
  const status = Number(error?.response?.status) || 0;
  const codeByStatus = {
    401: "AUTHENTICATION_REQUIRED",
    403: "ADMIN_ACCESS_REQUIRED",
    404: "COURSE_NOT_FOUND",
  };

  return { status, code: codeByStatus[status] || "COURSE_DELETE_UNAVAILABLE" };
};

const adminCourseCreateFailure = (error) => {
  const status = Number(error?.response?.status) || 0;
  const codeByStatus = {
    401: "AUTHENTICATION_REQUIRED",
    403: "ADMIN_ACCESS_REQUIRED",
    409: "COURSE_ALREADY_EXISTS",
  };

  return { status, code: codeByStatus[status] || "COURSE_CREATE_UNAVAILABLE" };
};



/**
 * Async thunk: fetch5courses.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetch5courses = createAsyncThunk(
    "courses/fetch5courses",
    async () => {
      const response = await axios.get(`Fetch5courses`);
      
      
      return response.data.courseslist
    }
  );

  /**
   * Async thunk: fetchcourseDetails.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const fetchcourseDetails = createAsyncThunk(
    "courses/fetchcourseDetails",
    async (id, { rejectWithValue, signal }) => {
      const courseId = String(id || "").trim();
      if (!courseId) {
        return rejectWithValue({ status: 0, code: "COURSE_UNAVAILABLE" });
      }

      try {
        const response = await axios.get(
          `course-details/${encodeURIComponent(courseId)}`,
          { signal }
        );
        const course = response?.data?.course;
        if (!isPublicCourseDetail(course, courseId)) {
          return rejectWithValue({ status: 0, code: "COURSE_UNAVAILABLE" });
        }
        return course;
      } catch (error) {
        if (signal?.aborted || error?.code === 'ERR_CANCELED') {
          throw error;
        }
        return rejectWithValue(publicCourseDetailFailure(error));
      }
    }
  );

  /**
   * Async thunk: Getcourses.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const Getcourses = createAsyncThunk(
    "courses/Getcourses",
    async () => {
      const response = await axios.get(`getcourses`);
     
      // 
      return response.data;
    }
  );

  /**
   * Async thunk: DeleteCourses.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const DeleteCourses = createAsyncThunk(
    "courses/DeleteCourses",
    async (id, { rejectWithValue }) => {
      try {
        const response = await axios.delete(`Delete_course/${id}`);
        return response.data;
      } catch (error) {
        return rejectWithValue(adminCourseDeleteFailure(error));
      }
    }
  );

  /**
   * Async thunk: CreateCourse.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const CreateCourse = createAsyncThunk(
    "courses/CreateCourse",
    async (formDataupdate, { rejectWithValue }) => {
      try {
        const response = await axios.post(`Create_Course`,formDataupdate);
        return response.data;
      } catch (error) {
        return rejectWithValue(adminCourseCreateFailure(error));
      }
    }
  );

  /**
   * Async thunk: updateCourse.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const updateCourse = createAsyncThunk(
  'courses/updateCourse',
  async ({ courseId, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`Update_Course/${courseId}`, updatedData);
      
      // Return the response data which should include courseId and updatedData
      return {
        courseId: courseId,
        updatedData: response.data.updatedData || updatedData,
        message: response.data.message
      };
    } catch (error) {
      // Return a proper error message
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update course';
      return rejectWithValue(errorMessage);
    }
  }
);

  /**
   * Async thunk: GetCourseByID.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const GetCourseByID = createAsyncThunk(
    "courses/GetCourseByID",
    async (id) => {
      
      try {
        const response = await axios.get(`GetCourseByID/${id}`);
        // 
        return response.data.Courses_ ;
      } catch (error) {
        
      }
    }
  );


  // SearchCoursebyName
  /**
   * Async thunk: SearchCoursebyName.
   *
   * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
   */
  export const SearchCoursebyName = createAsyncThunk(
    "courses/SearchCoursebyName",
    async (input) => {
      // 
      try {
        const response = await axios.post(`SearchCoursebyName/${input}`);
        // 
        return response;
      } catch (error) {
        
        return error.response.data.message;
      }
    }
  );

